import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { BarChart3, TrendingUp, FileSpreadsheet, Award, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDisplayClassName = (cls) => {
  if (!cls) return 'Unknown Class';
  return cls.class_name || cls.display_name || `${cls.grade_level || ''} ${cls.name || ''}`.trim() || cls.name || 'Unknown Class';
};

export default function AdminResultsDashboard() {
  const [overview, setOverview] = useState(null);
  const [classReports, setClassReports] = useState([]);
  const [termComparison, setTermComparison] = useState([]);
  const [studentAnalysis, setStudentAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [classSubjectPerformance, setClassSubjectPerformance] = useState([]);
  const [classPositions, setClassPositions] = useState([]);
  const [currentTerm, setCurrentTerm] = useState("Term 1");
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '' });
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  useEffect(() => {
    fetchAnnouncements();
    axios.get(`${API}/settings/active-term`).then(res => {
      setCurrentTerm(res.data.active_term || "Term 1");
    }).catch(() => {});
  }, []);

  const fetchAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const res = await axios.get(`${API}/announcements`);
      setAnnouncements(res.data || []);
    } catch (error) {
      toast.error('Failed to fetch announcements');
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  const handleAnnouncementChange = (e) => {
    setNewAnnouncement({ ...newAnnouncement, [e.target.name]: e.target.value });
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAnnouncement) {
        await axios.put(`${API}/announcements/${editingAnnouncement.id}`, newAnnouncement);
        toast.success('Announcement updated');
        setEditingAnnouncement(null);
      } else {
        await axios.post(`${API}/announcements`, newAnnouncement);
        toast.success('Announcement created');
      }
      setNewAnnouncement({ title: '', message: '' });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleEditAnnouncement = (a) => {
    setEditingAnnouncement(a);
    setNewAnnouncement({ title: a.title, message: a.message });
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await axios.delete(`${API}/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, classRes, termRes, studentRes] = await Promise.allSettled([
        axios.get(`${API}/dashboard/results-overview`),
        axios.get(`${API}/dashboard/class-results-report`),
        axios.get(`${API}/dashboard/term-comparison`),
        axios.get(`${API}/dashboard/student-results-analysis`),
      ]);

      const fallbackOverview = {
        total_results: 0,
        average_percentage: 0,
        pass_rate: 0,
        total_passed: 0,
        total_failed: 0,
        grade_distribution: [],
        top_subjects: [],
        bottom_subjects: [],
      };

      const nextOverview =
        overviewRes.status === "fulfilled"
          ? (overviewRes.value.data || fallbackOverview)
          : fallbackOverview;
      const nextClassReports =
        classRes.status === "fulfilled" ? (classRes.value.data || []) : [];
      const nextTermComparison =
        termRes.status === "fulfilled" ? (termRes.value.data || []) : [];
      const nextStudentAnalysis =
        studentRes.status === "fulfilled" ? (studentRes.value.data || []) : [];

      setOverview(nextOverview);
      setClassReports(nextClassReports);
      setTermComparison(nextTermComparison);
      setStudentAnalysis(nextStudentAnalysis);

      if (nextClassReports.length > 0) {
        setSelectedClassId(nextClassReports[0].id);
        fetchClassSubjectPerformance(nextClassReports[0].id);
      }

      const failedCount = [overviewRes, classRes, termRes, studentRes].filter(
        (r) => r.status === "rejected"
      ).length;

      if (failedCount > 0 && failedCount < 4) {
        toast.warning("Some analytics data could not be loaded right now.");
      }

    } catch (error) {
      toast.error("Failed to fetch results data: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchClassSubjectPerformance = async (classId) => {
    try {
      const res = await axios.get(`${API}/dashboard/class-subject-performance/${classId}`);
      setClassSubjectPerformance(res.data);
    } catch (error) {
      console.error("Failed to fetch subject performance");
    }
  };

  const fetchClassPositions = async (classId, term) => {
    try {
      const res = await axios.get(`${API}/results/class-positions/${classId}/${term}`);
      setClassPositions(res.data || []);
    } catch (error) {
      console.error("Failed to fetch class positions");
    }
  };

  const handleClassSelect = (classId) => {
    setSelectedClassId(classId);
    fetchClassSubjectPerformance(classId);
    fetchClassPositions(classId, currentTerm);
  };

  const getGradeColor = (grade) => {
    const g = String(grade);
    if (g.includes('1')) return "bg-emerald-100 text-emerald-800";
    if (g.includes('2')) return "bg-blue-100 text-blue-800";
    if (g.includes('3')) return "bg-cyan-100 text-cyan-800";
    if (g.includes('4')) return "bg-amber-100 text-amber-800";
    if (g.includes('5')) return "bg-orange-100 text-orange-800";
    return "bg-red-100 text-red-800";
  };

  const getUnitGrade = (percentage) => {
    if (percentage >= 90) return 1;
    if (percentage >= 80) return 2;
    if (percentage >= 70) return 3;
    if (percentage >= 60) return 4;
    if (percentage >= 50) return 5;
    return 6;
  };

  const getPercentageColor = (percentage) => {
    if (percentage >= 80) return "text-emerald-600 font-bold";
    if (percentage >= 60) return "text-blue-600 font-bold";
    if (percentage >= 40) return "text-amber-600 font-bold";
    return "text-red-600 font-bold";
  };

  const ProgressBar = ({ value, max = 100 }) => {
    const percentage = (value / max) * 100;
    const color = percentage >= 70 ? "bg-emerald-500" : percentage >= 50 ? "bg-amber-500" : "bg-red-500";

    return (
      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
        <div
          className={`${color} h-full rounded-full transition-all`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
          <BarChart3 className="text-pink-500" size={32} />
          Results Analytics Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Comprehensive analysis of academic results and performance metrics</p>
      </div>

      {/* Overview Statistics */}
      {overview && overview.total_results > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Results</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{overview.total_results}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileSpreadsheet className="text-blue-600" size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Average Score</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {overview.average_percentage}%
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <TrendingUp className="text-amber-600" size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Pass Rate</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">{overview.pass_rate}%</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Award className="text-emerald-600" size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Failure Rate</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">
                      {(100 - Number(overview.pass_rate || 0)).toFixed(2)}%
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <AlertCircle className="text-red-600" size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Unit Grade Distribution */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Unit Grade Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {overview.grade_distribution.map((item) => (
                  <div key={item.grade}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={getGradeColor(item.grade)}>{item.grade}</Badge>
                        <span className="text-sm text-slate-600">{item.count} results</span>
                      </div>
                      <span className="text-sm font-medium text-slate-900">{item.percentage}%</span>
                    </div>
                    <ProgressBar value={item.count} max={Math.max(...overview.grade_distribution.map(g => g.count))} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="text-center py-12 text-slate-500">
            <FileSpreadsheet size={48} className="mx-auto mb-4 opacity-50" />
            <p className="font-medium">No results data available yet</p>
            <p className="text-sm">Once students and results are added, analytics will appear here</p>
          </CardContent>
        </Card>
      )}

      {/* Detailed Reports */}
      <Tabs defaultValue="classes" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="classes" className="text-xs px-1">By Class</TabsTrigger>
          <TabsTrigger value="subjects" className="text-xs px-1">Subjects</TabsTrigger>
          <TabsTrigger value="terms" className="text-xs px-1">Terms</TabsTrigger>
          <TabsTrigger value="students" className="text-xs px-1">Students</TabsTrigger>
          <TabsTrigger value="announcements" className="text-xs px-1">Announce</TabsTrigger>
        </TabsList>

        {/* Class Results Report */}
        <TabsContent value="classes" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Class Performance Report</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-600">Class Name</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Students</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Results</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Avg %</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Pass Rate</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Unit 1</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Unit 6</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classReports && classReports.length > 0 ? (
                      classReports.map((cls) => (
                      <TableRow
                        key={cls.id}
                        onClick={() => handleClassSelect(cls.id)}
                        className="cursor-pointer hover:bg-slate-50"
                      >
                        <TableCell className="font-medium text-slate-900">
                          {getDisplayClassName(cls)}
                          <p className="text-xs text-slate-500 mt-1">Year {cls.year}</p>
                        </TableCell>
                        <TableCell className="text-right text-slate-900">
                          {cls.total_students || 0}
                        </TableCell>
                        <TableCell className="text-right text-slate-900">
                          {cls.total_results_entered || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={getPercentageColor(cls.avg_percentage || 0)}>
                            {Number(cls.avg_percentage || 0).toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            <span className="font-medium text-emerald-600">
                              {Number(cls.pass_rate || 0).toFixed(1)}%
                            </span>
                            <ProgressBar value={Number(cls.pass_rate || 0)} />
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="bg-emerald-50">
                            {cls.distinction_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="bg-red-50 text-red-700">
                            {cls.fail_count || 0}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan="7" className="text-center py-8 text-slate-500">
                          No classes available yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subject Performance */}
        <TabsContent value="subjects" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Top Performing Subjects</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {overview?.top_subjects?.map((subject, idx) => (
                  <div key={subject.subject}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-amber-600">#{idx + 1}</span>
                        <span className="font-medium text-slate-900">{subject.subject}</span>
                      </div>
                      <span className={getPercentageColor(subject.avg_score)}>
                        {Number(subject.avg_score || 0).toFixed(1)}%
                      </span>
                    </div>
                    <ProgressBar value={subject.avg_score} />
                    <p className="text-xs text-slate-500 mt-1">{subject.count} results entered</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Bottom Performing Subjects</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {overview?.bottom_subjects?.map((subject, idx) => (
                  <div key={subject.subject}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-red-600">⚠</span>
                        <span className="font-medium text-slate-900">{subject.subject}</span>
                      </div>
                      <span className={getPercentageColor(subject.avg_score)}>
                        {Number(subject.avg_score || 0).toFixed(1)}%
                      </span>
                    </div>
                    <ProgressBar value={subject.avg_score} />
                    <p className="text-xs text-slate-500 mt-1">{subject.count} results entered</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {selectedClassId && (
            <>
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">
                    {getDisplayClassName(classReports.find(c => c.id === selectedClassId))} - Subject Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-medium text-slate-600">Subject</TableHead>
                          <TableHead className="font-medium text-slate-600 text-right">Avg Score</TableHead>
                          <TableHead className="font-medium text-slate-600 text-right">Unit 1-2</TableHead>
                          <TableHead className="font-medium text-slate-600 text-right">Unit 3-4</TableHead>
                          <TableHead className="font-medium text-slate-600 text-right">Unit 5</TableHead>
                          <TableHead className="font-medium text-slate-600 text-right">Unit 6</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {classSubjectPerformance.map((subject) => (
                          <TableRow key={subject.subject}>
                            <TableCell className="font-medium text-slate-900">
                              {subject.subject}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={getPercentageColor(subject.avg_score)}>
                                {Number(subject.avg_score || 0).toFixed(1)}%
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge className="bg-emerald-100 text-emerald-800">
                                {subject.distinction_count}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge className="bg-blue-100 text-blue-800">
                                {subject.credit_count}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge className="bg-amber-100 text-amber-800">
                                {subject.pass_count}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge className="bg-red-100 text-red-800">
                                {subject.fail_count}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Class Rankings/Positions */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">
                    {getDisplayClassName(classReports.find(c => c.id === selectedClassId))} - Student Rankings ({currentTerm})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {classPositions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No results available for rankings yet
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="font-medium text-slate-600 text-center">Position</TableHead>
                            <TableHead className="font-medium text-slate-600">Student ID</TableHead>
                            <TableHead className="font-medium text-slate-600">Name</TableHead>
                            <TableHead className="font-medium text-slate-600 text-right">Total Score (out of 600)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {classPositions.map((student, index) => (
                            <TableRow key={student.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center">
                                  <Badge className={
                                    student.position === 1 ? 'bg-yellow-500 text-white' :
                                    student.position === 2 ? 'bg-gray-400 text-white' :
                                    student.position === 3 ? 'bg-orange-600 text-white' :
                                    'bg-slate-200 text-slate-800'
                                  }>
                                    {student.position}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell className="text-slate-900 font-medium">
                                {student.student_id_code}
                              </TableCell>
                              <TableCell className="text-slate-900">
                                {student.first_name} {student.last_name}
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {Number(student.total_score || 0).toFixed(0)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Term Comparison */}
        <TabsContent value="terms" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Term-by-Term Performance Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-600">Academic Year</TableHead>
                      <TableHead className="font-medium text-slate-600">Term</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Results</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Avg %</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Pass Rate</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Min Score</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Max Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {termComparison.map((term) => (
                      <TableRow key={`${term.academic_year}-${term.term}`}>
                        <TableCell className="font-medium text-slate-900">
                          {term.academic_year}
                        </TableCell>
                        <TableCell className="text-slate-900">{term.term}</TableCell>
                        <TableCell className="text-right text-slate-900">
                          {term.total_results}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={getPercentageColor(term.avg_percentage)}>
                            {Number(term.avg_percentage || 0).toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            <span className="font-medium text-emerald-600">
                              {Number(term.pass_rate || 0).toFixed(1)}%
                            </span>
                            <ProgressBar value={Number(term.pass_rate || 0)} />
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-slate-900">
                          {Number(term.min_score || 0).toFixed(1)}%
                        </TableCell>
                        <TableCell className="text-right text-slate-900">
                          {Number(term.max_score || 0).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Student Analysis */}
        <TabsContent value="students" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Student Performance Analysis</CardTitle>
              <p className="text-sm text-slate-500 mt-2">
                <strong>Distinction:</strong> 90% and above per subject
                <br />
                <strong>Unit Grading:</strong> 1 = 90-100, 2 = 80-89, 3 = 70-79, 4 = 60-69, 5 = 50-59, 6 = below 50
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-600">Student ID</TableHead>
                      <TableHead className="font-medium text-slate-600">Name</TableHead>
                      <TableHead className="font-medium text-slate-600">Class</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Subjects</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Overall Avg</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Passed (U1-5)</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Failed (U6)</TableHead>
                      <TableHead className="font-medium text-slate-600 text-right">Best/Worst</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {studentAnalysis.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium text-slate-900">
                          {student.student_id_code}
                        </TableCell>
                        <TableCell className="text-slate-900">
                          {student.first_name} {student.last_name}
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">
                          {student.class_name || "N/A"}
                        </TableCell>
                        <TableCell className="text-right text-slate-900">
                          {student.total_subjects || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={getPercentageColor(student.overall_average)}>
                            {Number(student.overall_average || 0).toFixed(1)}% (U{getUnitGrade(student.overall_average || 0)})
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className="bg-emerald-100 text-emerald-800">
                            {student.passed_subjects || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className="bg-red-100 text-red-800">
                            {student.failed_subjects || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-emerald-600 font-medium">
                              {Number(student.best_score || 0).toFixed(1)}%
                            </span>
                            <span className="text-red-600 font-medium">
                              {Number(student.worst_score || 0).toFixed(1)}%
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Term Management */}
        {/* Announcements Tab */}
        <TabsContent value="announcements" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Manage Announcements</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAnnouncementSubmit} className="space-y-4 mb-6">
                <Input
                  name="title"
                  placeholder="Announcement Title"
                  value={newAnnouncement.title}
                  onChange={handleAnnouncementChange}
                  required
                />
                <Input
                  name="message"
                  placeholder="Announcement Message"
                  value={newAnnouncement.message}
                  onChange={handleAnnouncementChange}
                  required
                />
                <div className="flex gap-2">
                  <Button type="submit">
                    {editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
                  </Button>
                  {editingAnnouncement && (
                    <Button type="button" variant="outline" onClick={() => {
                      setEditingAnnouncement(null);
                      setNewAnnouncement({ title: '', message: '' });
                    }}>
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </form>
              {loadingAnnouncements ? (
                <div>Loading...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {announcements.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.title}</TableCell>
                        <TableCell>{a.message}</TableCell>
                        <TableCell>{new Date(a.created_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditAnnouncement(a)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteAnnouncement(a.id)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
}